import {
    ReviewQueueCallout,
    type ReviewQueueReturn,
    reviewQueueReturnSchema,
    useReviewQueueNavigation,
} from '@client/components/_shared/auto-detect/review-queue-return';
import { ComplaintForm } from '@client/components/complaints/complaint-form';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import {
    buildFormDraftDataType,
    ORG_FLEX_STORAGE_DATA_TYPES,
    resolveFormDraftSource,
} from '@shared/constants/system/org-flex-storage';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/compliance/complaints/add')({
    staticData: {
        metadata: {
            title: 'New complaint',
        },
        ui: {
            title: 'New complaint',
            widthVariant: 'compact',
            // Wider than the compact default (w-4xl) so the description
            // textarea and the paired fields get breathing room.
            className: 'w-6xl max-w-full',
        },
    },
    validateSearch: zodValidator(
        z.object({
            messageClassificationId: z.coerce.bigint().optional().catch(undefined),
            // Set when logging a complaint from a compliance audit finding
            // (CUR-168 / REQ-FND-7) — links the new complaint back to the finding.
            complianceAuditFindingId: z.coerce.bigint().optional().catch(undefined),
            // claimId may flow in from the auto-detect drawer when the
            // message mentions a single resolvable claim — pre-links the
            // complaint to that claim and lets ComplaintForm's claim
            // fetch prefill `policyNo`.
            claimId: z.coerce.bigint().optional().catch(undefined),
            incident: z
                .object({
                    incidentId: z.coerce.bigint().optional().catch(undefined),
                    handlingPartyIds: z.array(z.coerce.bigint()).optional().catch(undefined),
                    ownerId: z.coerce.bigint().optional().catch(undefined),
                    products: z.array(z.number()).optional().catch(undefined),
                    policyNo: z.string().optional().catch(undefined),
                    externalClaimId: z.string().optional().catch(undefined),
                })
                .optional()
                .catch(undefined),
            // Set when an auto-detect review was confirmed with "Then create a complaint record":
            // saving continues the review queue (or opens the incident form first) instead of
            // opening the new complaint.
            reviewQueue: reviewQueueReturnSchema.optional().catch(undefined),
        }),
    ),
    component: RouteComponent,
});

function RouteComponent() {
    const orgId = useOrgId();
    const { messageClassificationId, complianceAuditFindingId, claimId, incident, reviewQueue } = Route.useSearch();
    const navigate = useNavigate();
    const queue = useReviewQueueNavigation();
    // CUR-1825: the router keeps this component mounted when only the search params
    // change, so key the form by its draft key. A new source then remounts the form
    // and hydrates that source's draft instead of autosaving the old values under it.
    const draftKey = buildFormDraftDataType(
        ORG_FLEX_STORAGE_DATA_TYPES.COMPLAINT_DRAFT,
        resolveFormDraftSource('complaint', {
            complianceAuditFindingId,
            messageClassificationId,
            incidentId: incident?.incidentId,
            claimId,
        }),
    );

    // The incident form that follows the complaint in a review. No explicit complaint link: the
    // server links the two through their shared message, which needs only the access the reviewer
    // just used on that message, whereas an explicit link needs write access to the complaint, which
    // the reviewer lacks once it is assigned to another owner or outside their handling party.
    function openIncidentFollowUp(reviewQueue: ReviewQueueReturn) {
        navigate({
            to: '/console/$orgId/compliance/incidents/add',
            params: { orgId },
            search: { messageClassificationId, reviewQueue: { nextId: reviewQueue.nextId } },
        });
    }

    return (
        <Card>
            <ReviewQueueCallout record="complaint" reviewQueue={reviewQueue} />
            <ComplaintForm
                key={draftKey}
                claimId={claimId}
                messageClassificationId={messageClassificationId}
                complianceAuditFindingId={complianceAuditFindingId}
                incidentData={incident}
                onCancel={() =>
                    reviewQueue ? queue.backToMessage(messageClassificationId) : navigate({ moveInHistory: 'back' })
                }
                onAfterSaveAccessDenied={() => {
                    if (reviewQueue?.followUp === 'incident') {
                        // The reviewer confirmed both records, so the incident form still follows,
                        // even though they cannot see the complaint they just saved.
                        openIncidentFollowUp(reviewQueue);
                        return;
                    }
                    if (reviewQueue) {
                        queue.continueQueue(reviewQueue);
                        return;
                    }
                    navigate({
                        to: '/console/$orgId/compliance/complaints',
                        params: {
                            orgId,
                        },
                    });
                }}
                onAfterSaveCancel={({ id: complaintId }) => {
                    if (reviewQueue?.followUp === 'incident') {
                        // Both a complaint and an incident: the incident form follows, linked to
                        // the complaint just saved, and returns to the queue in turn.
                        openIncidentFollowUp(reviewQueue);
                        return;
                    }
                    if (reviewQueue) {
                        queue.continueQueue(reviewQueue);
                        return;
                    }
                    return incident?.incidentId
                        ? navigate({
                              to: '/console/$orgId/compliance/incidents/details/$id/manage/complaints',
                              params: {
                                  orgId,
                                  id: incident.incidentId,
                              },
                          })
                        : navigate({
                              to: '/console/$orgId/compliance/complaints/details/$id/manage',
                              params: {
                                  orgId,
                                  id: complaintId,
                              },
                          });
                }}
            />
        </Card>
    );
}
